import React, { useMemo, useState } from "react";
import {
  CardComp,
  TabsComp,
  ModalCompAdvanceUpdate,
  ButtonComp,
  TextInputComp,
  FormikDatePicker,
  FormikTextAreaComp,
  TableCompData,
} from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import Message from "../../../components/MessageModal";
import { AdminMemberExitRequestsHeader } from "../../../utils/TableHeader";
import {
  useGetMemberExitRequestsQuery,
  useGetStaffMemberExitRequestQuery,
  useApproveMemberExitRequestMutation,
  useDeclineMemberExitRequestMutation,
  useMarkMemberExitPaidMutation,
} from "../../../store/admin/member-exit";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import toast from "react-hot-toast";
import { formatMoney } from "../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../utils/helpers";

function ExitRequestsTab({
  status,
  page,
  setPage,
  onApprove,
  onDecline,
  onMarkPaid,
  onViewDetails,
}) {
  const { data, isFetching } = useGetMemberExitRequestsQuery({
    status,
    page,
    limit: 20,
  });

  const tableData = useMemo(() => {
    if (!data?.requests) return [];
    return data.requests.map((request) => ({
      ...request,
      serialNumber: request.id,
    }));
  }, [data]);

  const pagination = data?.pagination || {};

  return (
    <div>
      <TableCompData
        marginBottom={"72px"}
        columns={AdminMemberExitRequestsHeader({
          onViewDetails,
          onApprove,
          onDecline,
          onMarkPaid,
        })}
        data={tableData}
        progressPending={isFetching}
        progressComponent={<TableSkeletonLoader rows={10} columns={7} />}
        message={
          !isFetching && (
            <EmptyTable
              Message={`No ${status === "all" ? "" : `${status} `}exit requests`}
            />
          )
        }
        cellPadding="12px"
      />
      {!isFetching && tableData?.length > 0 && (
        <TablePagination
          marginTop={"0"}
          handlePageChange={setPage}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          total={pagination.total}
          count={pagination.count}
          perPage={pagination.perPage}
        />
      )}
    </div>
  );
}

function getStatusColor(status) {
  const key = (status || "").toUpperCase();
  if (key === "PAID") return "#19BC31";
  if (key === "APPROVED") return "#31778C";
  if (key === "PENDING") return "#FFA500";
  if (key === "DECLINED" || key === "CANCELED") return "#FF4343";
  return "#A1A0A0";
}

function DetailRow({ label, children, last }) {
  return (
    <div
      className={`d-flex justify-content-between align-items-start gap-3 py-3 ${
        last ? "" : "border-bottom"
      }`}
      style={{ borderColor: "#e0e0e0" }}
    >
      <span className="text-blacklight small mb-0" style={{ minWidth: 120 }}>
        {label}
      </span>
      <div className="text-end fw-semibold text-black1 small mb-0">{children}</div>
    </div>
  );
}

function MoneyLine({ label, amount, hint, emphasize, muted }) {
  return (
    <div
      className={`d-flex justify-content-between align-items-start py-2 ${
        emphasize ? "pt-3 mt-1 border-top" : ""
      }`}
      style={{ borderColor: "#e0e0e0" }}
    >
      <div>
        <div
          className={emphasize ? "fw-bold text-black2" : "fw-semibold text-black2"}
          style={{ fontSize: emphasize ? 15 : 14 }}
        >
          {label}
        </div>
        {hint ? (
          <div className="small text-blacklight mt-1" style={{ maxWidth: 260 }}>
            {hint}
          </div>
        ) : null}
      </div>
      <div
        className={`fw-bold ${muted ? "text-blacklight" : emphasize ? "text-primary" : "text-black1"}`}
        style={{ fontSize: emphasize ? 18 : 14, whiteSpace: "nowrap" }}
      >
        {formatMoney(Number(amount || 0).toFixed(2), true, "₦")}
      </div>
    </div>
  );
}

function ViewExitDetails({
  requestId,
  onClose,
  onApprove,
  onDecline,
  onMarkPaid,
}) {
  const {
    data: detail,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetStaffMemberExitRequestQuery(requestId, {
    skip: !requestId,
  });

  const status = (detail?.status || "").toUpperCase();
  const memberName = detail?.member
    ? `${detail.member.first_name || ""} ${detail.member.last_name || ""}`.trim()
    : "—";
  const memberNo = detail?.member?.member_no || detail?.member_id || "—";

  if (isLoading || (isFetching && !detail)) {
    return (
      <div className="w-full py-4 px-3 px-lg-4" aria-busy="true">
        <TableSkeletonLoader rows={6} columns={2} />
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="w-full py-4 px-3 px-lg-4">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
          <h5 className="fw-bold text-black1 mb-0">Exit request</h5>
          <img
            src="/Icon/cancel.svg"
            role="button"
            onClick={onClose}
            alt="Close"
            style={{ cursor: "pointer", height: 20 }}
          />
        </div>
        <p className="text-muted mb-3">
          {error?.data?.message ||
            error?.message ||
            "Exit request not found."}
        </p>
        <ButtonComp
          containerClassName="w-auto"
          btnText="Retry"
          BorderColor="var(--color-primary)"
          btnClassName="py-2 px-4"
          onClick={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="w-full py-4 px-3 px-lg-4">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-black1 mb-1" style={{ fontSize: 20 }}>
            Exit Request #{detail.id}
          </h5>
          <p className="small text-blacklight mb-0">
            Submitted{" "}
            {detail.created_at
              ? moment(detail.created_at).format("DD-MMM-YYYY · hh:mm A")
              : "—"}
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span
            className="px-3 py-1 rounded-pill text-white fw-semibold"
            style={{
              backgroundColor: getStatusColor(status),
              fontSize: 12,
              minWidth: 100,
              textAlign: "center",
            }}
          >
            {convertToTitleCase(status)}
          </span>
          <img
            src="/Icon/cancel.svg"
            role="button"
            onClick={onClose}
            alt="Close"
            style={{ cursor: "pointer", height: 20 }}
          />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <div
            className="h-100 rounded-4 p-3"
            style={{ background: "#fafafa", border: "1px solid #e0e0e0" }}
          >
            <div
              className="small text-blacklight fw-semibold mb-2 text-uppercase"
              style={{ letterSpacing: "0.04em", fontSize: 11 }}
            >
              Member
            </div>
            <DetailRow label="Name">{memberName || "—"}</DetailRow>
            <DetailRow label="Membership No.">#{memberNo}</DetailRow>
            <DetailRow label="Email" last>
              {detail.member?.email || "—"}
            </DetailRow>
          </div>
        </div>
        <div className="col-lg-6">
          <div
            className="h-100 rounded-4 p-3"
            style={{ background: "#fafafa", border: "1px solid #e0e0e0" }}
          >
            <div
              className="small text-blacklight fw-semibold mb-2 text-uppercase"
              style={{ letterSpacing: "0.04em", fontSize: 11 }}
            >
              Payout bank
            </div>
            {detail.bank_account ? (
              <>
                <DetailRow label="Bank">
                  {detail.bank_account.bank_name || "—"}
                </DetailRow>
                <DetailRow label="Account No.">
                  <span className="font-monospace">
                    {detail.bank_account.account_number || "—"}
                  </span>
                </DetailRow>
                <DetailRow label="Account name" last>
                  {detail.bank_account.account_name || "—"}
                </DetailRow>
              </>
            ) : (
              <p className="small text-danger mb-0">No bank account on file.</p>
            )}
          </div>
        </div>
      </div>

      {detail.member_note ? (
        <div
          className="rounded-4 p-3 mb-3"
          style={{
            background: "rgba(49, 119, 140, 0.08)",
            border: "1px solid rgba(49, 119, 140, 0.25)",
          }}
        >
          <div className="small fw-semibold text-black2 mb-1">Member note</div>
          <p className="mb-0 text-black1" style={{ lineHeight: 1.55 }}>
            {detail.member_note}
          </p>
        </div>
      ) : null}

      <div
        className="rounded-4 p-3 mb-3"
        style={{ background: "#fff", border: "1px solid #e0e0e0" }}
      >
        <div
          className="small text-blacklight fw-semibold mb-2 text-uppercase"
          style={{ letterSpacing: "0.04em", fontSize: 11 }}
        >
          Settlement snapshot
        </div>
        <MoneyLine label="Savings (net)" amount={detail.savings_amount} />
        <MoneyLine label="Shares" amount={detail.shares_amount} />
        <MoneyLine
          label="Other"
          amount={detail.other_amount}
          hint="Shown for transparency — not included in payout"
          muted
        />
        <MoneyLine label="Payout due" amount={detail.payout_amount} emphasize />
        <div
          className="row g-2 mt-2 pt-2 border-top"
          style={{ borderColor: "#e0e0e0" }}
        >
          <div className="col-sm-6">
            <div className="small text-blacklight">Outstanding loans at submit</div>
            <div className="fw-semibold text-black1">
              {formatMoney(
                Number(detail.outstanding_loan_amount || 0).toFixed(2),
                true,
                "₦"
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="small text-blacklight">Unpaid fines at submit</div>
            <div className="fw-semibold text-black1">
              {formatMoney(
                Number(detail.outstanding_fine_amount || 0).toFixed(2),
                true,
                "₦"
              )}
            </div>
          </div>
        </div>
      </div>

      {detail.decline_reason ? (
        <div
          className="rounded-4 p-3 mb-3"
          style={{
            background: "rgba(255, 67, 67, 0.08)",
            border: "1px solid #e0a8ae",
          }}
        >
          <div className="small fw-semibold text-danger mb-1">Decline reason</div>
          <p className="mb-0 small text-black1">{detail.decline_reason}</p>
        </div>
      ) : null}

      {(detail.payment_date ||
        detail.approved_by ||
        detail.declined_by ||
        detail.paid_by) && (
        <div
          className="rounded-4 p-3 mb-3"
          style={{ background: "#fafafa", border: "1px solid #e0e0e0" }}
        >
          <div
            className="small text-blacklight fw-semibold mb-2 text-uppercase"
            style={{ letterSpacing: "0.04em", fontSize: 11 }}
          >
            Staff activity
          </div>
          {detail.approved_by && (
            <DetailRow label="Approved by">
              {detail.approved_by.first_name} {detail.approved_by.last_name}
            </DetailRow>
          )}
          {detail.declined_by && (
            <DetailRow label="Declined by">
              {detail.declined_by.first_name} {detail.declined_by.last_name}
            </DetailRow>
          )}
          {detail.paid_by && (
            <DetailRow label="Paid by">
              {detail.paid_by.first_name} {detail.paid_by.last_name}
            </DetailRow>
          )}
          {detail.payment_date && (
            <DetailRow label="Payment date" last={!detail.payment_reference}>
              {detail.payment_date}
            </DetailRow>
          )}
          {detail.payment_reference && (
            <DetailRow label="Reference" last>
              {detail.payment_reference}
            </DetailRow>
          )}
        </div>
      )}

      {(status === "PENDING" || status === "APPROVED") && (
        <div
          className="d-flex flex-row flex-wrap justify-content-end align-items-center gap-2 pt-3 mt-1 border-top"
          style={{ borderColor: "#e0e0e0" }}
        >
          {status === "PENDING" && onDecline && (
            <ButtonComp
              containerClassName="w-auto"
              btnText="Decline"
              BorderColor="#FF4343"
              btnClassName="py-2 px-4"
              ButtonInlineStyle={{
                color: "#FF4343",
                minWidth: 120,
                borderRadius: 10,
                whiteSpace: "nowrap",
              }}
              onClick={() => onDecline(detail.id)}
            />
          )}
          {status === "PENDING" && onApprove && (
            <ButtonComp
              containerClassName="w-auto"
              btnText="Approve"
              btnClassName="border-0 text-white py-2 px-4"
              ButtonInlineStyle={{
                backgroundColor: "#19BC31",
                minWidth: 120,
                borderRadius: 10,
                whiteSpace: "nowrap",
              }}
              onClick={() => onApprove(detail.id)}
            />
          )}
          {status === "APPROVED" && onMarkPaid && (
            <ButtonComp
              containerClassName="w-auto"
              btnText="Mark as paid"
              btnClassName="border-0 text-white py-2 px-4"
              ButtonInlineStyle={{
                backgroundColor: "#31778C",
                minWidth: 140,
                borderRadius: 10,
                whiteSpace: "nowrap",
              }}
              onClick={() => onMarkPaid(detail.id)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function DeclineExitModal({ requestId, onDecline, onClose, isLoading }) {
  return (
    <div className="py-4 px-3 px-lg-2">
      <div className="d-flex justify-content-between align-items-start mb-3 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-black1 mb-1">Decline exit request</h5>
          <p className="small text-blacklight mb-0">
            The member will be notified with your reason.
          </p>
        </div>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={onClose}
          alt="Close"
          style={{ cursor: "pointer", height: 20 }}
        />
      </div>
      <Formik
        initialValues={{ decline_reason: "" }}
        validationSchema={Yup.object({
          decline_reason: Yup.string().required("Reason is required"),
        })}
        onSubmit={async (values, { resetForm }) => {
          await onDecline(requestId, values.decline_reason);
          resetForm();
        }}
      >
        <Form>
          <FormikTextAreaComp
            rows={5}
            fieldName="decline_reason"
            LabelText="Reason for declining"
            placeholder="Explain why this exit request is declined"
          />
          <div className="d-flex flex-wrap justify-content-end gap-2 pt-4">
            <ButtonComp
              containerClassName="w-auto"
              btnText="Cancel"
              BorderColor="var(--color-primary)"
              btnClassName="py-2 px-4"
              ButtonInlineStyle={{ minWidth: 120, borderRadius: 10 }}
              onClick={onClose}
            />
            <ButtonComp
              containerClassName="w-auto"
              loading={isLoading}
              type="submit"
              btnText="Decline request"
              ButtonInlineStyle={{
                backgroundColor: "#FF4343",
                minWidth: 140,
                borderRadius: 10,
              }}
              btnClassName="border-0 text-white py-2 px-4"
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
}

function MarkPaidExitModal({ requestId, onMarkPaid, onClose, isLoading }) {
  return (
    <div className="py-4 px-3 px-lg-2">
      <div className="d-flex justify-content-between align-items-start mb-3 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-black1 mb-1">Mark payout as paid</h5>
          <p className="small text-blacklight mb-0">
            This completes the exit. The member will no longer be able to sign
            in.
          </p>
        </div>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={onClose}
          alt="Close"
          style={{ cursor: "pointer", height: 20 }}
        />
      </div>
      <Formik
        initialValues={{
          payment_date: moment().format("YYYY-MM-DD"),
          payment_reference: "",
        }}
        validationSchema={Yup.object({
          payment_date: Yup.date().required("Payment date is required"),
          payment_reference: Yup.string().max(255),
        })}
        onSubmit={async (values, { resetForm }) => {
          await onMarkPaid(
            requestId,
            values.payment_date,
            values.payment_reference
          );
          resetForm();
        }}
      >
        <Form>
          <FormikDatePicker
            fieldName="payment_date"
            LabelText="Payment date"
            maxDate={moment().toDate()}
          />
          <div className="mt-3">
            <TextInputComp
              name="payment_reference"
              LabelText="Payment reference (optional)"
              type="text"
              placeholder="Transaction reference"
              borderColor="#A1A0A0"
            />
          </div>
          <div className="d-flex flex-wrap justify-content-end gap-2 pt-4">
            <ButtonComp
              containerClassName="w-auto"
              btnText="Cancel"
              BorderColor="var(--color-primary)"
              btnClassName="py-2 px-4"
              ButtonInlineStyle={{ minWidth: 120, borderRadius: 10 }}
              onClick={onClose}
            />
            <ButtonComp
              containerClassName="w-auto"
              loading={isLoading}
              type="submit"
              btnText="Mark paid & exit"
              ButtonInlineStyle={{
                backgroundColor: "#19BC31",
                minWidth: 160,
                borderRadius: 10,
              }}
              btnClassName="border-0 text-white py-2 px-4"
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
}

export default function MemberExitRequests() {
  const [modalPage, setModalPage] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [pendingPage, setPendingPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [declinedPage, setDeclinedPage] = useState(1);
  const [paidPage, setPaidPage] = useState(1);
  const [allPage, setAllPage] = useState(1);

  const [approveExit, { isLoading: isApproving }] =
    useApproveMemberExitRequestMutation();
  const [declineExit, { isLoading: isDeclining }] =
    useDeclineMemberExitRequestMutation();
  const [markPaid, { isLoading: isMarking }] = useMarkMemberExitPaidMutation();

  const closeModal = () => {
    setModalPage("");
    setSelectedRequestId(null);
  };

  const handleApprove = async (requestId) => {
    try {
      await approveExit(requestId).unwrap();
      toast.success("Exit request approved");
      closeModal();
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Failed to approve");
      closeModal();
    }
  };

  const handleDecline = async (requestId, reason) => {
    try {
      await declineExit({ requestId, decline_reason: reason }).unwrap();
      toast.success("Exit request declined");
      closeModal();
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Failed to decline");
      closeModal();
    }
  };

  const handleMarkPaid = async (requestId, paymentDate, paymentReference) => {
    try {
      await markPaid({
        requestId,
        payment_date: paymentDate,
        payment_reference: paymentReference || null,
      }).unwrap();
      toast.success("Payout recorded. Member marked as exited.");
      closeModal();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || "Failed to mark paid"
      );
      closeModal();
    }
  };

  const useModal = [
    {
      name: "Approve Modal",
      component: (
        <Message
          title="Confirm Approval"
          loading={isApproving}
          desc="Approve this membership exit request? You can mark the payout as paid afterward."
          onBack={closeModal}
          onProceed={() => handleApprove(selectedRequestId)}
        />
      ),
    },
    {
      name: "Decline Modal",
      component: (
        <DeclineExitModal
          requestId={selectedRequestId}
          onDecline={handleDecline}
          onClose={closeModal}
          isLoading={isDeclining}
        />
      ),
    },
    {
      name: "Mark as Paid Modal",
      component: (
        <MarkPaidExitModal
          requestId={selectedRequestId}
          onMarkPaid={handleMarkPaid}
          onClose={closeModal}
          isLoading={isMarking}
        />
      ),
    },
    {
      name: "View Details Modal",
      component: selectedRequestId ? (
        <ViewExitDetails
          key={selectedRequestId}
          requestId={selectedRequestId}
          onClose={closeModal}
          onApprove={(id) => {
            setSelectedRequestId(id);
            setModalPage("Approve Modal");
          }}
          onDecline={(id) => {
            setSelectedRequestId(id);
            setModalPage("Decline Modal");
          }}
          onMarkPaid={(id) => {
            setSelectedRequestId(id);
            setModalPage("Mark as Paid Modal");
          }}
        />
      ) : (
        <div className="py-4 px-3">
          <TableSkeletonLoader rows={6} columns={2} />
        </div>
      ),
    },
  ];

  const useTab = [
    {
      name: "Pending",
      component: (
        <ExitRequestsTab
          status="pending"
          page={pendingPage}
          setPage={setPendingPage}
          onApprove={(id) => {
            setSelectedRequestId(id);
            setModalPage("Approve Modal");
          }}
          onDecline={(id) => {
            setSelectedRequestId(id);
            setModalPage("Decline Modal");
          }}
          onViewDetails={(id) => {
            setSelectedRequestId(id);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "Approved",
      component: (
        <ExitRequestsTab
          status="approved"
          page={approvedPage}
          setPage={setApprovedPage}
          onMarkPaid={(id) => {
            setSelectedRequestId(id);
            setModalPage("Mark as Paid Modal");
          }}
          onViewDetails={(id) => {
            setSelectedRequestId(id);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "Declined",
      component: (
        <ExitRequestsTab
          status="declined"
          page={declinedPage}
          setPage={setDeclinedPage}
          onViewDetails={(id) => {
            setSelectedRequestId(id);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "Completed",
      component: (
        <ExitRequestsTab
          status="paid"
          page={paidPage}
          setPage={setPaidPage}
          onViewDetails={(id) => {
            setSelectedRequestId(id);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "All",
      component: (
        <ExitRequestsTab
          status="all"
          page={allPage}
          setPage={setAllPage}
          onViewDetails={(id) => {
            setSelectedRequestId(id);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
  ];

  return (
    <section className="px-lg-3">
      <CardComp cardClassName={"border-0 ps-3 mb-3"}>
        <div>
          <h3 className="text-black1 fw-bold mb-0">Membership Exit Requests</h3>
          <p className="text-muted mb-0">
            Review exit requests, approve payouts of savings + shares, then mark
            paid to complete exit.
          </p>
        </div>
      </CardComp>

      <CardComp cardClassName={"border-0 ps-3"}>
        <div className="py-4"></div>
        <TabsComp
          TabArray={useTab}
          input="name"
          justifyContent={"justify-content-start"}
          tabClassName="px-0"
          TabArrayClassName="px-4"
        />
      </CardComp>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={closeModal}
        centered
        size={modalPage === "View Details Modal" ? "xl" : "lg"}
      />
    </section>
  );
}
